import React, { useEffect, useState, useRef } from 'react';
import { motion, easeOut } from 'framer-motion';
import LinkSection from '../sections/LinkSection';
import TextSection from '../sections/TextSection';
import ImageSection from '../sections/ImageSection';
import { Helmet } from 'react-helmet-async';

import './ProjectPage.scss';
import Lenis from 'lenis';



const ProjectPage = ({ page }) => {
    const [mainImageUrls, setMainImageUrls] = useState({ webp: '', jpg: '' });
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    // Refs for DOM nodes
    const mainPictureRef = useRef(null);
    const columnContentRef = useRef(null);
    const columnContentInnerRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768); // Adjust the width as needed
        };

        handleResize(); // Check on mount
        window.addEventListener('resize', handleResize); // Check on resize

        return () => {
            window.removeEventListener('resize', handleResize); // Cleanup
        };
    }, []);

    // Smooth/slow scroll inside the desktop column-content only
    useEffect(() => {
        if (isMobile) return;
        if (!columnContentRef.current || !columnContentInnerRef.current) return;

        const lenis = new Lenis({
            wrapper: columnContentRef.current,
            content: columnContentInnerRef.current,
            wheelEventsTarget: columnContentRef.current,
            touchEventsTarget: columnContentRef.current,
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.9,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, [isMobile]);



    // Calculate and set padding-bottom for column-content (desktop only)
    useEffect(() => {
        if (!mainPictureRef.current || !columnContentRef.current) {
            // console.log('Refs not ready yet');
            return;
        }

        function updatePadding() {
            /*    if (isMobile) {
                   // console.log('Mobile view - clearing padding');
                   columnContentRef.current.style.paddingBottom = '15px'; // footer height mobile
   
                   return;
               } */

            // Get the image element (not the container)
            const image = mainPictureRef.current.querySelector('img');
            if (!image) return;

            // Get the image's position relative to the viewport
            const imageRect = image.getBoundingClientRect();

            // Calculate the distance from the bottom of the image to the bottom of the viewport
            const distance = window.innerHeight - imageRect.bottom;

            // Add some minimum padding (e.g., 20px) to ensure there's always some space
            // const minPadding = 20;
            const paddingValue = distance - 40;

            // Apply the padding to the column content
            columnContentRef.current.style.paddingBottom = `${paddingValue}px`;
        }

        /// console.log('Setting up padding effect', { isMobile, isImageLoaded });
        updatePadding();
        window.addEventListener('resize', updatePadding);

        return () => {
            // console.log('Cleaning up padding effect');
            window.removeEventListener('resize', updatePadding);
        };
    }, [isMobile, isImageLoaded]);

    const getOptimizedImageUrl = (image, format = 'jpg') => {
        if (!image?.fields?.file) return '';

        const originalWidth = image.fields.file.details.image.width;
        const originalHeight = image.fields.file.details.image.height;
        const windowWidth = window.innerWidth;

        let targetWidth;
        if (windowWidth < 768) {
            targetWidth = Math.min(windowWidth * 0.9, originalWidth);
        } else {
            targetWidth = Math.min(500, originalWidth);
        }

        const targetHeight = Math.round((targetWidth / originalWidth) * originalHeight);
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2.5);
        const requestWidth = Math.round(targetWidth * pixelRatio);
        const requestHeight = Math.round(targetHeight * pixelRatio);

        setImageDimensions({ width: Math.round(targetWidth), height: targetHeight });

        return `${image.fields.file.url}?w=${requestWidth}&h=${requestHeight}&fm=${format}&fit=fill&q=${format === 'webp' ? 90 : 85}`;
    };

    useEffect(() => {
        if (page?.fields?.mainPicture) {
            const updateImageUrls = () => {
                setMainImageUrls({
                    webp: getOptimizedImageUrl(page.fields.mainPicture, 'webp'),
                    jpg: getOptimizedImageUrl(page.fields.mainPicture, 'jpg')
                });
            };
            updateImageUrls();
            window.addEventListener('resize', updateImageUrls);
            return () => window.removeEventListener('resize', updateImageUrls);
        }
    }, [page]);

    if (!page || !page.fields || !page.fields.sections) {
        return <div>Loading...</div>; // Or some other placeholder
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();
        const ordinal = (d) => {
            if (d > 3 && d < 21) return d + 'th';
            switch (d % 10) {
                case 1: return d + 'st';
                case 2: return d + 'nd';
                case 3: return d + 'rd';
                default: return d + 'th';
            }
        };
        return `${ordinal(day)} ${month} ${year}`;
    };

    const canonicalUrl = `https://nthng.nl/${page.url}`;
    const pageDescription = page.fields.sections?.[0]?.fields?.text?.substring(0, 160) || `${page.title} by nthng - ${page.category} release. Listen and explore this electronic music project.`;
    const ogImage = page.fields.mainPicture?.fields?.file?.url ? `https:${page.fields.mainPicture.fields.file.url}` : 'https://nthng.nl/content/nthng_unfinished.webp';

    // Structured data for music release
    const musicReleaseSchema = {
        "@context": "https://schema.org",
        "@type": "MusicRelease",
        "name": page.title,
        "description": pageDescription,
        "url": canonicalUrl,
        "image": ogImage,
        "byArtist": {
            "@type": "MusicGroup",
            "name": "nthng",
            "url": "https://nthng.nl"
        },
        "datePublished": page.date,
        "genre": ["Electronic", "Techno"]
    };

    return (
        <>
            <Helmet>
                <title>{page.title} - nthng</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={`${page.title} - nthng`} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:type" content="music.song" />
                <meta property="og:site_name" content="nthng" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${page.title} - nthng`} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={ogImage} />
                <script type="application/ld+json">
                    {JSON.stringify(musicReleaseSchema)}
                </script>
            </Helmet>
            <motion.div
                className='project-page'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: easeOut }}
            >
                {!isMobile ? (
                    <div className='project-page-desktop'>
                        <div className='column-main-picture' ref={mainPictureRef}>
                            <picture>
                                <source srcSet={mainImageUrls.webp} type="image/webp" />
                                <source srcSet={mainImageUrls.jpg} type="image/jpeg" />
                                <img
                                    src={mainImageUrls.jpg || page.fields.mainPicture?.fields?.file?.url}
                                    alt={page.fields.mainPicture?.fields?.title || ''}
                                    width={imageDimensions.width}
                                    height={imageDimensions.height}
                                    onLoad={() => setIsImageLoaded(true)}
                                    className={`main-image ${isImageLoaded ? 'loaded' : ''}`}
                                />
                            </picture>
                        </div>

                        <div className='column-content' ref={columnContentRef}>
                            <div className='column-content-inner' ref={columnContentInnerRef}>
                                <div className='top-bar'>
                                    <h1>{page.title}</h1>
                                    {page.buyLink && (
                                        <p className='buy-button'>
                                            <a href={page.buyLink} target='_blank' rel='noopener noreferrer'>buy</a>
                                        </p>
                                    )}
                                </div>

                                <p className='category-date'>{page.category} • {formatDate(page.date)}</p>
                                {page.fields.sections.map((section, index) => {
                                    const contentTypeId = section?.sys?.contentType?.sys?.id;
                                    switch (contentTypeId) {
                                        case 'linkSection':
                                            return <LinkSection key={index} {...(section.fields || {})} />;
                                        case 'textSection':
                                            return <TextSection key={index} {...(section.fields || {})} />;
                                        case 'imageSection':
                                            return <ImageSection key={index} {...(section.fields || {})} />;
                                        default:
                                            return null;
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='project-page-mobile'>
                        <div className="main-picture">
                            <picture>
                                <source srcSet={mainImageUrls.webp} type="image/webp" />
                                <source srcSet={mainImageUrls.jpg} type="image/jpeg" />
                                <img
                                    src={mainImageUrls.jpg || page.fields.mainPicture?.fields?.file?.url}
                                    alt={page.fields.mainPicture?.fields?.title || ''}
                                    width={imageDimensions.width}
                                    height={imageDimensions.height}
                                    onLoad={() => setIsImageLoaded(true)}
                                    className={`main-image ${isImageLoaded ? 'loaded' : ''}`}
                                />
                            </picture>
                        </div>

                        <div className='column-content' style={{ paddingBottom: '15px' }}>
                            <div className='top-bar'>
                                <h1>{page.title}</h1>
                                {page.buyLink && (
                                    <p className='buy-button'>
                                        <a href={page.buyLink} target='_blank' rel='noopener noreferrer'>buy</a>
                                    </p>
                                )}
                            </div>

                            <p className='category-date'>{page.category} • {formatDate(page.date)}</p>
                            {page.fields.sections.map((section, index) => {
                                const contentTypeId = section?.sys?.contentType?.sys?.id;
                                switch (contentTypeId) {
                                    case 'linkSection':
                                        return <LinkSection key={index} {...(section.fields || {})} />;
                                    case 'textSection':
                                        return <TextSection key={index} {...(section.fields || {})} />;
                                    case 'imageSection':
                                        return <ImageSection key={index} {...(section.fields || {})} />;
                                    default:
                                        return null;
                                }
                            })}
                        </div>
                    </div>
                )}
            </motion.div>
        </>
    );
};

export default ProjectPage;